// @ts-ignore
import React, { useCallback, useContext, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import { flex } from '@fabric-ui/theme';
import { Box, ClickAwayListener, Pagination, PaginationItem, Table, useTheme } from '@mui/material';
import TableContainer from '@mui/material/TableContainer';
import {
  ColumnDef,
  ColumnFiltersState,
  ColumnOrderState,
  ColumnSizingState,
  functionalUpdate,
  getCoreRowModel,
  getFacetedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  SortingState,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table';
import { ConditionalWrapper } from '../ConditionalWrapper';
import { AvalorTableIds, PaginationRowsText } from '../Table';
import AvTableV2Body from './AvTableV2Body';
import AvTableV2Head from './AvTableV2Head';
import AvTableV2Loading from './AvTableV2Loading';
import { AvTableV2Context } from './AvTableV2Provider';
import { activeRowIdFeature } from './CustomFeatures/activeRowId.feature';
import { flatFacetedUniqueValues, globalFilterFn } from './filterFns';
import { InlineActionsCellRenderer } from './InlineActions';
import {
  ClientSideControlledPagination,
  DataRecord,
  IAvTableV2Base,
  ServerSideControlledPagination,
  TableSizeEnum,
  TableV2PaginationType,
} from './table.types';
import { defaultColumnCell, getAccessorKeyFn, getSizeKey, useColumnResizeEnd } from './table.utils';
import { useRowSelection } from './useRowSelection';

const MAX_SORT_COLUMNS = 1;
const ROWS_PER_PAGE = 20;

const defaultColumn = {
  cell: defaultColumnCell,
  width: 'auto',
  minSize: 100,
  filterFn: globalFilterFn,
};

export const AvTableV2Base = (props: IAvTableV2Base) => {
  const {
    rows = [],
    headCells,
    loading = false,
    multiSelect = false,
    size = TableSizeEnum.Small,
    activeRowId,
    onActiveRowIdChange,
    sorting: controlledSorting,
    onSortingChange: setControlledSorting,
    pagination: paginationProps = {
      type: TableV2PaginationType.CLIENT_NOT_CONTROLLED,
      rowsPerPage: ROWS_PER_PAGE,
    },
    rowSelection: controlledRowSelection,
    onRowSelectionChange: setControlledRowSelection,
    columnFilters: controlledColumnFilters,
    onColumnFiltersChange: setControlledColumnFilters,
    initialState,
    enableActiveRowHighlighting = true,
    sx = {},
    onScroll,
    onIsAllRowsSelectedChange,
    disableRowClickNavigation,
    inlineActions,
    hasHeaderFilters = true,
    allowKeyboardNavigation = false,
    resizable = false,
    storageKey,
    shouldDisplayNull,
    tanstackTableProps,
    onNavigate: navigate,
    localColumnsWidth: LocalColumnsWidth,
    onColumnResizeEnd: setLayoutWidths,
    FieldFilter,
  } = props;

  const theme = useTheme();
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const isRowSelectionControlled = controlledRowSelection !== undefined;
  const [internalRowSelection, setInternalRowSelection] = useState({});
  const rowSelection = isRowSelectionControlled ? controlledRowSelection : internalRowSelection;
  const onRowSelectionChange = isRowSelectionControlled ? setControlledRowSelection : setInternalRowSelection;

  const isSortingControlled = controlledSorting !== undefined;
  const [internalSorting, setInternalSorting] = useState<SortingState>(initialState?.sorting || []);
  const sorting: SortingState = isSortingControlled ? controlledSorting : internalSorting;
  const onSortingChange = isSortingControlled ? setControlledSorting : setInternalSorting;

  const paginationType = paginationProps?.type;
  const isServerSidePagination = paginationType === TableV2PaginationType.SERVER_CONTROLLED;
  const isClientControlledPagination = paginationType === TableV2PaginationType.CLIENT_CONTROLLED;
  const isClientUncontrolledPagination = paginationType === TableV2PaginationType.CLIENT_NOT_CONTROLLED;
  const rowsPerPage = !paginationType
    ? 1000
    : (isServerSidePagination || isClientControlledPagination ? paginationProps.state?.pageSize : paginationProps.rowsPerPage) ||
      ROWS_PER_PAGE;
  const totalRows = isServerSidePagination ? (paginationProps as ServerSideControlledPagination).totalRows : undefined;

  const controlledPagination =
    isServerSidePagination || isClientControlledPagination
      ? (paginationProps as ServerSideControlledPagination | ClientSideControlledPagination).state
      : undefined;
  const onControlledPaginationChange =
    isServerSidePagination || isClientControlledPagination
      ? (paginationProps as ServerSideControlledPagination | ClientSideControlledPagination).onPaginationChange
      : undefined;
  const isPaginationControlled = !!controlledPagination;

  const [internalPagination, setInternalPagination] = useState<PaginationState>(() => {
    const initialPagination = initialState?.pagination;
    return {
      pageIndex: initialPagination?.pageIndex ?? 0,
      pageSize: initialPagination?.pageSize ?? rowsPerPage,
    };
  });

  const pagination: PaginationState = isPaginationControlled ? controlledPagination : internalPagination;

  const onPaginationChange = isPaginationControlled ? onControlledPaginationChange : setInternalPagination;

  const isPaginationEnabled =
    (isServerSidePagination || isClientControlledPagination || isClientUncontrolledPagination) && !!paginationType;

  const isColumnFiltersControlled = controlledColumnFilters !== undefined;
  const [internalColumnFilters, setInternalColumnFilters] = useState<ColumnFiltersState>(initialState?.columnFilters || []);
  const columnFilters = isColumnFiltersControlled ? controlledColumnFilters : internalColumnFilters;
  const onColumnFiltersChange = isColumnFiltersControlled ? setControlledColumnFilters : setInternalColumnFilters;

  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(
    (resizable && storageKey && LocalColumnsWidth
      ? Object.keys(LocalColumnsWidth).reduce((acc, key) => {
          const value = LocalColumnsWidth[key];
          return typeof value === 'string' ? acc : { ...acc, [key]: value };
        }, {})
      : {}) as ColumnSizingState
  );

  const [containerWidth, setContainerWidth] = useState(0);
  const keyName = useMemo(() => headCells.find(hc => hc.meta?.isKey)?.id, [headCells]);

  const {
    isClickedAll,
    setIsClickedAll,
    showSelectAllRowsButton,
    setShowSelectedAllRowsButton,
    renderSelectAllHeader,
    renderSelectCell,
    handleSelectAllRowsClick,
  } = useRowSelection({ onIsAllRowsSelectedChange });

  const columns = useMemo(() => {
    const selectAllColumn: ColumnDef<DataRecord> = {
      id: AvalorTableIds.SelectAllId,
      header: renderSelectAllHeader,
      cell: renderSelectCell,
      size: 50,
      minSize: 50,
      enableSorting: false,
      enableResizing: false,
      enableColumnFilter: false,
    };

    return [
      ...(multiSelect ? [selectAllColumn] : []),
      ...headCells.map(hc => ({
        ...hc,
        accessorFn: getAccessorKeyFn(hc.id),
        meta: { ...hc.meta, isCustomCell: !!hc.cell },
        minSize: hc.meta?.isKey ? 50 : 100,
        width: 'auto',
      })),
      ...(resizable
        ? [
            {
              id: AvalorTableIds.FreeSpaceId,
              header: '',
              cell: () => null,
              enableResizing: false,
              enableSorting: false,
              enableColumnFilter: false,
            },
          ]
        : []),
      ...(inlineActions?.length
        ? [
            {
              id: AvalorTableIds.InlineActionsId,
              header: '',
              enableSorting: false,
              enableResizing: false,
              enableColumnFilter: false,
              cell: InlineActionsCellRenderer,
              size: 0,
              maxSize: 0,
              minSize: 0,
            },
          ]
        : []),
    ] as ColumnDef<DataRecord, any>[];
  }, [headCells, multiSelect, rows, inlineActions, resizable]);

  const { columnVisibility, columnOrder } = useMemo(
    () =>
      columns.reduce<{
        columnVisibility: VisibilityState;
        columnOrder: ColumnOrderState;
      }>(
        (acc, hc) => ({
          ...acc,
          columnVisibility: { ...acc.columnVisibility, [hc.id!]: !hc.meta?.hidden },
          columnOrder: !hc.meta?.hidden ? [...acc.columnOrder, hc.id!] : acc.columnOrder,
        }),
        { columnVisibility: {}, columnOrder: [] }
      ),
    [columns]
  );

  const isActiveRowControlled = !!onActiveRowIdChange;

  const defaultOnRowClick = (row: DataRecord) => {
    if (disableRowClickNavigation?.(row)) {
      return;
    }

    if (enableActiveRowHighlighting && !onActiveRowIdChange) {
      navigate({ to: `edit/${row.id}`, params: prev => ({ ...prev }) });
    }
  };
  const onActiveRowIdChangeHandler = useCallback(
    getNewActiveRowId => {
      const newActiveRowId = functionalUpdate(getNewActiveRowId, activeRowId ?? undefined);
      onActiveRowIdChange?.(newActiveRowId);
    },
    [activeRowId, onActiveRowIdChange]
  );

  const onRowSelectionChangeHandler = useCallback(
    getNewSelection => {
      const newSelection = functionalUpdate(getNewSelection, rowSelection);
      onRowSelectionChange?.(newSelection, rows);
    },
    [onRowSelectionChange, rows, rowSelection]
  );

  const onPaginationChangeHandler = useCallback(
    getNewPagination => {
      const newPagination = functionalUpdate(getNewPagination, pagination);
      onPaginationChange?.(newPagination);
    },
    [onPaginationChange, pagination]
  );

  const onColumnSizingChangeHandler = useCallback(
    getNewColumnSizing => {
      const newSizing = functionalUpdate(getNewColumnSizing, columnSizing);
      setColumnSizing(newSizing);
    },
    [columnSizing]
  );

  const onSortingChangeHandler = useCallback(
    getNewSort => {
      const newSorting = functionalUpdate(getNewSort, sorting);
      onSortingChange?.(newSorting);
      onPaginationChange?.({ pageSize: rowsPerPage, pageIndex: 0 });
    },
    [isSortingControlled, onSortingChange, sorting, rowsPerPage]
  );

  const onColumnFiltersChangeHandler = useCallback(
    getNewFilters => {
      const newFilters = functionalUpdate(getNewFilters, columnFilters);
      const cleanFilters = newFilters?.filter(fr => !!(fr.value as string[])?.length);
      onColumnFiltersChange?.(cleanFilters);
      onPaginationChange?.({ pageSize: rowsPerPage, pageIndex: 0 });
    },
    [columnFilters, isColumnFiltersControlled, onColumnFiltersChange, rowsPerPage]
  );

  const table = useReactTable({
    data: rows,
    columns,
    defaultColumn,
    _features: [activeRowIdFeature],
    state: {
      rowSelection,
      pagination,
      sorting,
      columnVisibility,
      columnOrder,
      activeRowId,
      columnFilters,
      columnSizing,
    },
    meta: {
      onRowClick: defaultOnRowClick,
      disableRowClickNavigation,
      inlineActions,
      shouldDisplayNull,
    },
    enableColumnResizing: resizable,
    columnResizeMode: 'onChange',
    getRowId: row => (keyName ? `${row[keyName]}` : ''),
    autoResetPageIndex: false,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: isServerSidePagination,
    manualSorting: isServerSidePagination && isSortingControlled,
    rowCount: totalRows,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableColumnFilters: hasHeaderFilters,
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: flatFacetedUniqueValues(),
    enableActiveRow: isActiveRowControlled,
    isMultiSortEvent: () => false,
    maxMultiSortColCount: MAX_SORT_COLUMNS,
    onActiveRowIdChange: onActiveRowIdChangeHandler,
    onRowSelectionChange: onRowSelectionChangeHandler,
    onPaginationChange: onPaginationChangeHandler,
    onColumnSizingChange: onColumnSizingChangeHandler,
    onSortingChange: onSortingChangeHandler,
    onColumnFiltersChange: onColumnFiltersChangeHandler,
    ...tanstackTableProps,
  });

  const columnSizeVars = useMemo(
    () => ({
      ...table.getFlatHeaders().reduce(
        (acc, header) => ({
          ...acc,
          [getSizeKey(header.id, 'header')]: header.getSize(),
          [getSizeKey(header.column.id, 'col')]: header.getSize(),
        }),
        {}
      ),
      '--container-width': `${containerWidth}px`,
      '--total-defined-column-size': `${table.getTotalSize()}px`,
    }),
    [table.getState().columnSizingInfo, table.getState().columnSizing, containerWidth]
  );
  const onArrowKeyEvent = useEffectEvent((event: KeyboardEvent) => {
    if ((event.code === 'ArrowDown' || event.code === 'ArrowUp') && activeRowId) {
      event.preventDefault();
      const { rows } = table.getRowModel();
      const currentIndex = rows.findIndex(row => row.id === activeRowId);
      if (currentIndex >= 0) {
        const nextRow = event.code === 'ArrowDown' ? rows[currentIndex + 1] : rows[currentIndex - 1];
        if (nextRow) {
          onActiveRowIdChange?.(nextRow.id);
          const rowElement = tableContainerRef.current?.querySelector(`[data-row-id='${nextRow.id}']`);
          rowElement?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    }
  });

  useEffect(() => {
    if (allowKeyboardNavigation) {
      window.addEventListener('keydown', onArrowKeyEvent);
    }
    return () => window.removeEventListener('keydown', onArrowKeyEvent);
  }, [allowKeyboardNavigation, onArrowKeyEvent]);

  const clearSelect = () => {
    table.toggleAllRowsSelected(false);
    setShowSelectedAllRowsButton(false);
    setIsClickedAll(false);
    if (onIsAllRowsSelectedChange) {
      onIsAllRowsSelectedChange(false);
    }
  };

  const clearOnClickAwayWrapper = (children: React.ReactElement) => (
    <ClickAwayListener onClickAway={clearSelect}>{children}</ClickAwayListener>
  );

  useColumnResizeEnd(table, newSizing => {
    if (storageKey && resizable) {
      setLayoutWidths(storageKey, newSizing);
    }
  });

  useEffect(() => {
    if (tableContainerRef.current) {
      const observer = new ResizeObserver(entries => setContainerWidth(entries[0]?.contentRect.width ?? 0));
      observer.observe(tableContainerRef.current);
      return () => observer.disconnect();
    }
    return undefined;
  }, []);

  useEffect(() => {
    if (isRowSelectionControlled && Object.keys(rowSelection).length === 0 && showSelectAllRowsButton) {
      clearSelect();
    }
  }, [isRowSelectionControlled, rowSelection, setIsClickedAll, setShowSelectedAllRowsButton]);

  return (
    <AvTableV2Context.Provider value={table}>
      <Box sx={[{ ...flex.col, height: '100%', overflow: 'auto' }, ...(Array.isArray(sx) ? sx : [sx])]}>
        <TableContainer ref={tableContainerRef} onScroll={e => onScroll?.(e, tableContainerRef)}>
          <Table
            stickyHeader
            size={size}
            style={
              resizable
                ? {
                    ...columnSizeVars,
                    width: 'max(var(--container-width, 0px), var(--total-defined-column-size, 0px))',
                    tableLayout: 'fixed',
                  }
                : { width: '100%' }
            }>
            <AvTableV2Head tableContainerRef={tableContainerRef} FieldFilter={FieldFilter} />
            {loading ? (
              <AvTableV2Loading totalRows={totalRows} pageSize={pagination.pageSize} size={size} />
            ) : (
              <ConditionalWrapper condition={multiSelect} wrapper={clearOnClickAwayWrapper}>
                <AvTableV2Body
                  showSelectAllRowsButton={showSelectAllRowsButton}
                  tableContainerRef={tableContainerRef}
                  clearSelect={clearSelect}
                  setIsClickedAll={setIsClickedAll}
                  isClickedAll={isClickedAll}
                  handleSelectAllRowsClick={() => handleSelectAllRowsClick(table)}
                  enableActiveRowHighlighting={enableActiveRowHighlighting}
                />
              </ConditionalWrapper>
            )}
          </Table>
        </TableContainer>
        <Box sx={{ ...flex.justifyEndCenter, flexShrink: 0 }}>
          {isPaginationEnabled && (
            <>
              <PaginationRowsText
                from={1 + table.getState().pagination.pageIndex * table.getState().pagination.pageSize}
                to={Math.min(
                  (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                  totalRows || table.getFilteredRowModel().rows.length
                )}
                count={totalRows || table.getFilteredRowModel().rows.length}
              />
              <Pagination
                shape="rounded"
                count={totalRows ? Math.ceil(totalRows / table.getState().pagination.pageSize) : table.getPageCount()}
                page={table.getState().pagination.pageIndex + 1}
                onChange={(event, page) => table.setPageIndex(page - 1)}
                sx={{ py: 1, pr: 1 }}
                renderItem={item => (
                  <PaginationItem
                    component="div"
                    {...item}
                    page={item.page?.toLocaleString()}
                    sx={{
                      fontSize: 12,
                      height: 24,
                      minWidth: 24,
                      margin: '0px 1px',
                      color:
                        item.page === table.getState().pagination.pageIndex + 1
                          ? theme.palette.colors.neutrals[900]
                          : theme.palette.colors.neutrals[600],
                      fontWeight: item.page === table.getState().pagination.pageIndex + 1 ? 600 : 400,
                    }}
                  />
                )}
              />
            </>
          )}
        </Box>
      </Box>
    </AvTableV2Context.Provider>
  );
};
